/* eslint-disable class-methods-use-this */
import React from 'react';

import { Row, Col } from 'react-bootstrap';
import Aux from "../../hoc/_Aux/Aux";
import '../../styles/scss/style.scss';

class TermsOfUse extends React.Component {
  render() {
    return (
      <Aux>
        <Row>
          <Col>
            <div className="card-body">

              <h1>Terms of Use — Bailo.app</h1>
              <p><em>Last updated: July 2026</em></p>
              <p>Please read these Terms of Use carefully before using our Service.</p>

              <h2>1. Interpretation and Definitions</h2>
              <h3>Interpretation</h3>
              <p>Capitalized words have the meanings defined below. These definitions apply equally in singular and plural form.</p>
              <h3>Definitions</h3>
              <ul>
                <li><p><strong>Company</strong> (also "we", "us", "our") refers to Bailo.app.</p></li>
                <li><p><strong>Service</strong> refers to the Bailo.app mobile application (iOS and Android), the website accessible at <a href="https://bailo.app" rel="noreferrer external nofollow noopener" target="_blank">https://bailo.app</a>, and any related platforms or tools offered by the Company.</p></li>
                <li><p><strong>App User</strong> means any individual who accesses the Service through the mobile app or website to browse, search, or interact with dance-related content (venues, events, schools), without necessarily creating a full account.</p></li>
                <li><p><strong>Organizer</strong> means any individual or entity that registers on the website platform to create and manage content such as venues, tango schools, milongas, or dance events.</p></li>
                <li><p><strong>Anonymous Profile</strong> means a lightweight user profile created automatically upon first use of the mobile app, identified by a device-generated installation ID and an optional display name, without requiring a password or email address.</p></li>
                <li><p><strong>Device Installation ID</strong> means a unique identifier generated by the app on first launch and stored securely on the user's device, used to associate preferences, likes, and push notifications with that device.</p></li>
                <li><p><strong>Content</strong> means any text, images, descriptions, schedules, links, or other information posted or uploaded to the Service by Organizers or the Company.</p></li>
                <li><p><strong>Like</strong> means the action by which an App User saves or marks a venue or event as a favourite within the Service.</p></li>
                <li><p><strong>Push Notification</strong> means an alert message sent by the Company to an App User's device, related to venues or events the user has previously liked or expressed interest in.</p></li>
                <li><p><strong>Country</strong> refers to: Spain.</p></li>
                <li><p><strong>You</strong> means the individual or entity accessing or using the Service.</p></li>
              </ul>

              <h2>2. Scope and Acceptance</h2>
              <p>These Terms govern the use of the Service by all users, including App Users and Organizers. By accessing or using the Service in any form, you agree to be bound by these Terms.</p>
              <p>If you do not agree with any part of these Terms, you may not use the Service.</p>
              <p>You represent that you are at least 18 years old. The Service is not directed at individuals under 18.</p>
              <p>These Terms apply to:</p>
              <ul>
                <li>App Users who browse, like, or receive notifications about dance venues and events.</li>
                <li>Organizers who register on the platform to publish and manage venue or event listings.</li>
              </ul>

              <h2>3. Two Types of Users</h2>
              <h3>3.1 App Users (mobile app and website visitors)</h3>
              <p>App Users can access the Service without creating a traditional account. Upon first use of the mobile app, a lightweight Anonymous Profile is created, consisting of:</p>
              <ul>
                <li>A device-generated installation ID stored on your device.</li>
                <li>An optional display name you provide.</li>
                <li>An optional email address, used solely to recover your data if you change devices.</li>
              </ul>
              <p>App Users can:</p>
              <ul>
                <li>Browse and search venues, milongas, tango schools, and events worldwide.</li>
                <li>Like venues and events to receive relevant push notifications.</li>
                <li>Read and interact with Organizer-published content.</li>
                <li>Manage their notification preferences and request deletion of their data at any time.</li>
              </ul>
              <h3>3.2 Organizers (website platform)</h3>
              <p>Organizers register on the website with a full account (email and password) to:</p>
              <ul>
                <li>Create and manage listings for venues, schools, milongas, and events.</li>
                <li>Access analytics about their listings (views, likes, audience location).</li>
                <li>Communicate with the Company regarding their listings.</li>
                <li>Send push notifications to App Users who have liked their venue or event, subject to the Company's approval and tools.</li>
              </ul>
              <p>Organizers are responsible for the accuracy, legality, and appropriateness of all Content they publish.</p>

              <h2>4. Anonymous Profile and Push Notifications</h2>
              <h3>4.1 How it works</h3>
              <p>When you first open the Bailo.app mobile application, you will be prompted to:</p>
              <ul>
                <li>Enter a display name (required).</li>
                <li>Optionally enter your email address.</li>
                <li>Accept these Terms of Use and the Privacy Policy.</li>
              </ul>
              <p>By completing this step, an Anonymous Profile is created in our systems. No password is required. Your device installation ID serves as your unique identifier.</p>
              <h3>4.2 Push notifications</h3>
              <p>By creating an Anonymous Profile and granting notification permissions on your device, you consent to receive push notifications from Bailo.app. These notifications will be related exclusively to:</p>
              <ul>
                <li>Venues or events you have liked.</li>
                <li>Updates from Organizers whose venues or events you follow.</li>
                <li>General announcements from Bailo.app relevant to your location.</li>
              </ul>
              <p>You may disable push notifications at any time through your device's system settings or within the app's settings section.</p>
              <h3>4.3 Device change</h3>
              <p>If you uninstall and reinstall the app on the same device, your Anonymous Profile will be recovered automatically using your device installation ID.</p>
              <p>If you change to a new device, your previous profile and likes cannot be automatically recovered unless you provided an email address during onboarding. In that case, your data will be linked to your new device upon reinstallation.</p>
              <h3>4.4 Data deletion</h3>
              <p>You may request permanent deletion of your Anonymous Profile and all associated data (likes, preferences, notification history) at any time through the app's Settings section. Upon deletion, your data will be permanently removed from our active systems within 30 days. Backups are purged within 6 months.</p>

              <h2>5. Organizer Accounts</h2>
              <h3>5.1 Account creation</h3>
              <p>To publish content as an Organizer, you must register with a valid email address and create a secure password. You agree to provide accurate and up-to-date information.</p>
              <h3>5.2 Account security</h3>
              <p>You are responsible for maintaining the confidentiality of your account credentials. You must notify us immediately at <a href="mailto:bailo.app@gmail.com">bailo.app@gmail.com</a> if you suspect any unauthorized access to your account.</p>
              <h3>5.3 Content responsibility</h3>
              <p>Organizers are solely responsible for all Content they publish. By publishing Content, you represent and warrant that:</p>
              <ul>
                <li>You own the Content or have the necessary rights to publish it.</li>
                <li>The Content does not infringe any third-party rights.</li>
                <li>The Content is accurate, lawful, and not misleading.</li>
                <li>The Content complies with these Terms and all applicable laws.</li>
              </ul>
              <h3>5.4 Content licence</h3>
              <p>By publishing Content on the Service, you grant Bailo.app a non-exclusive, worldwide, royalty-free licence to display, reproduce, and distribute your Content within the Service for the purpose of making it available to App Users. You retain ownership of your Content.</p>

              <h2>6. Content Restrictions</h2>
              <p>All users (App Users and Organizers) agree not to post, upload, or transmit any Content that:</p>
              <ul>
                <li>Is unlawful, fraudulent, defamatory, obscene, or harmful.</li>
                <li>Infringes intellectual property rights of any third party.</li>
                <li>Contains malware, viruses, or any harmful code.</li>
                <li>Constitutes unauthorized advertising or spam.</li>
                <li>Impersonates any person or entity.</li>
                <li>Violates the privacy of any individual.</li>
                <li>Is unrelated to dance, music, or the tango community.</li>
              </ul>
              <p>The Company reserves the right to remove any Content that violates these restrictions, without prior notice and at its sole discretion.</p>

              <h2>7. Analytics and Venue Metrics</h2>
              <p>Organizers with an active listing on Bailo.app have access to an analytics dashboard showing aggregated and anonymised metrics about their listing, including:</p>
              <ul>
                <li>Number of likes received.</li>
                <li>Number of profile views and description reads.</li>
                <li>Search impressions.</li>
                <li>Audience distribution by city and country.</li>
              </ul>
              <p>All analytics data is derived from anonymous device interactions and does not include personally identifiable information about individual App Users.</p>

              <h2>8. Intellectual Property</h2>
              <p>The Service and its original content, design, features, and functionality are the exclusive property of Bailo.app and its licensors, protected by copyright, trademark, and other applicable laws.</p>
              <p>You may not use the Bailo.app name, logo, or trademarks without prior written consent from the Company.</p>

              <h2>9. Links to Third-Party Websites</h2>
              <p>The Service may contain links to third-party websites or services. Bailo.app has no control over and assumes no responsibility for the content, privacy policies, or practices of any third-party sites. We strongly encourage you to review the terms and privacy policies of any third-party site you visit.</p>

              <h2>10. Termination</h2>
              <h3>App Users</h3>
              <p>You may delete your Anonymous Profile at any time through the app. The Company may deactivate an Anonymous Profile if it is associated with abusive behaviour or Terms violations.</p>
              <h3>Organizers</h3>
              <p>The Company may suspend or terminate an Organizer account immediately, without prior notice, for breach of these Terms, publication of prohibited Content, or conduct harmful to the Service or its community.</p>
              <p>Upon termination, your right to use the Service ceases immediately. Content published by a terminated Organizer may be removed at the Company's discretion.</p>

              <h2>11. Limitation of Liability</h2>
              <p>To the maximum extent permitted by applicable law, Bailo.app shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Service, including loss of data, loss of profits, or service interruptions.</p>
              <p>The Company's total liability shall not exceed EUR 100 or the amount paid by you to the Company in the twelve months preceding the claim, whichever is greater.</p>
              <p>The Service is provided "as is" and "as available" without warranties of any kind, express or implied.</p>

              <h2>12. Governing Law and Dispute Resolution</h2>
              <p>These Terms are governed by the laws of Spain, without regard to conflict of law provisions.</p>
              <p>If you are a consumer resident in the European Union, you benefit from the mandatory provisions of the law of your country of residence.</p>
              <p>For any dispute, you agree to first contact us at <a href="mailto:bailo.app@gmail.com">bailo.app@gmail.com</a> to seek an informal resolution. If unresolved, disputes shall be submitted to the competent courts of Spain.</p>

              <h2>13. Changes to These Terms</h2>
              <p>We reserve the right to modify these Terms at any time. If changes are material, we will provide at least 30 days' notice through the app or by email (if provided). Continued use of the Service after changes take effect constitutes acceptance of the revised Terms.</p>

              <h2>14. Severability</h2>
              <p>If any provision of these Terms is found to be unenforceable, that provision shall be modified to the minimum extent necessary to make it enforceable, and the remaining provisions shall continue in full force.</p>

              <h2>15. Contact</h2>
              <p>For questions about these Terms:</p>
              <ul>
                <li>Email: <a href="mailto:bailo.app@gmail.com">bailo.app@gmail.com</a></li>
                <li>Website: <a href="https://bailo.app" rel="noreferrer external nofollow noopener" target="_blank">https://bailo.app</a></li>
              </ul>

            </div>
          </Col>
        </Row>
      </Aux>
    );
  }
}

export default TermsOfUse;
